// Argus — FilePreviewPanel: preview text / JSON / image via ArgusDialog reusable.
// Detectează kind din extension. Pentru text: fetch artifact ca text. Pentru image: <img src>.

import { useEffect, useState } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Alert,
  Link,
} from '@mui/material';
import { ArgusDialog } from '../../components/floating/ArgusDialog';
import { previewKindFromName, type PreviewKind } from '../../lib/fileRead';
import type { MseVideoSource } from '../../lib/videoStream';

export interface FilePreviewPanelProps {
  open: boolean;
  onClose: () => void;
  fileName: string;
  fullPath: string;
  loading: boolean;
  error: string | null;
  downloadUrl: string | null;
  /** Bloc ws-direct-batch-3: MSE source pentru video >10 MB streaming. Caller decide creare/dispose. */
  mseSource?: MseVideoSource | null;
}

export function FilePreviewPanel({
  open, onClose, fileName, fullPath, loading, error, downloadUrl, mseSource = null,
}: FilePreviewPanelProps): JSX.Element {
  const kind: PreviewKind = previewKindFromName(fileName);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !downloadUrl || kind !== 'text') {
      setTextContent(null);
      return;
    }
    let cancelled = false;
    setTextLoading(true);
    setTextError(null);
    // Browser request without Range header → full body (200 OK with our updated controller).
    fetch(downloadUrl, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
        const text = await res.text();
        if (!cancelled) setTextContent(text);
      })
      .catch((err: Error) => {
        if (!cancelled) setTextError(err.message);
      })
      .finally(() => {
        if (!cancelled) setTextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, downloadUrl, kind]);

  const titleNode = (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {fileName}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
        {fullPath}
      </Typography>
    </Box>
  );

  return (
    <ArgusDialog
      open={open}
      onClose={onClose}
      title=""
      maxWidth="md"
      contentSx={{ minHeight: 320, display: 'flex', flexDirection: 'column' }}
      actions={
        <>
          {downloadUrl ? (
            <Button component="a" href={downloadUrl} download={fileName} target="_blank" rel="noopener">
              Download
            </Button>
          ) : null}
          <Button onClick={onClose} variant="contained">Close</Button>
        </>
      }
    >
      <Box sx={{ mb: 2 }}>{titleNode}</Box>
      {loading ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2">Reading file from agent…</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !downloadUrl ? (
        // Bloc ws-direct-batch-2 2026-05-05: artifactId devine null pe WS path (transient — NO disk).
        // Conditia anterioara `!artifactId || !downloadUrl` afișa „No artifact" pe WS happy path.
        // Acum verificăm DOAR downloadUrl (Blob URL pentru WS, REST URL pentru fallback >10 MB).
        <Typography variant="body2" color="text.secondary">No preview available.</Typography>
      ) : kind === 'text' ? (
        textLoading ? (
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        ) : textError ? (
          <Alert severity="error">{textError}</Alert>
        ) : (
          <Box
            component="pre"
            sx={{
              flexGrow: 1,
              m: 0,
              p: 1.5,
              fontSize: 12,
              fontFamily: 'monospace',
              backgroundColor: 'action.hover',
              borderRadius: 1,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              userSelect: 'text',
            }}
          >
            {textContent ?? ''}
          </Box>
        )
      ) : kind === 'image' ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
          <Box
            component="img"
            src={downloadUrl}
            alt={fileName}
            crossOrigin="use-credentials"
            sx={{ maxWidth: '100%', maxHeight: 480, objectFit: 'contain' }}
          />
        </Box>
      ) : kind === 'video' ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 0.5 }}>
          <Box
            component="video"
            ref={(el: HTMLVideoElement | null) => {
              if (el && mseSource) mseSource.attachVideoElement(el);
            }}
            src={downloadUrl}
            controls
            preload="metadata"
            sx={{ maxWidth: '100%', maxHeight: '70vh', outline: 'none' }}
          />
        </Box>
      ) : kind === 'video_unsupported' ? (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="body2" color="warning.main">
            Video format not supported in browser preview.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Use Download below to play locally with a media player.
          </Typography>
          <Link href={downloadUrl} target="_blank" rel="noopener" download={fileName}>
            Download file
          </Link>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            File type not supported for inline preview.
          </Typography>
          <Link href={downloadUrl} target="_blank" rel="noopener" download={fileName}>
            Download file
          </Link>
        </Box>
      )}
    </ArgusDialog>
  );
}
